/**
 * External application openers (URL scheme based).
 *
 * Used by the editor header toolbar to launch the active file in an external
 * editor / file manager. All entries rely on the host platform handling the
 * URL scheme — we delegate via `platform.openExternal()`.
 */

import type { LucideIcon } from "lucide-react";
import { Code2, ExternalLink, FolderOpen, MousePointer2, Zap } from "lucide-react";
import { t as translate } from "@magic-md/i18n";

export interface ExternalOpener {
    id: string;
    label: string;
    icon: LucideIcon;
    /** Build the URL that will be passed to `openExternal()`. */
    url: (filePath: string) => string;
}

/**
 * Encode a POSIX-style absolute path for safe inclusion in a URL scheme,
 * preserving path separators.
 */
function encodeFilePath(filePath: string): string {
    return filePath
        .split("/")
        .map((seg) => encodeURIComponent(seg))
        .join("/");
}

function tr(key: string): string {
    return translate("editor", key);
}

export const EXTERNAL_OPENERS: ExternalOpener[] = [
    {
        id: "vscode",
        get label() { return tr("externalOpeners.openWithVSCode"); },
        icon: Code2,
        url: (p) => `vscode://file${encodeFilePath(p)}`,
    },
    {
        id: "cursor",
        get label() { return tr("externalOpeners.openWithCursor"); },
        icon: MousePointer2,
        url: (p) => `cursor://file${encodeFilePath(p)}`,
    },
    {
        id: "zed",
        get label() { return tr("externalOpeners.openWithZed"); },
        icon: Zap,
        url: (p) => `zed://file${encodeFilePath(p)}`,
    },
    {
        id: "system",
        get label() { return tr("externalOpeners.openWithSystem"); },
        icon: ExternalLink,
        url: (p) => `file://${encodeFilePath(p)}`,
    },
];

/** Reveal-in-finder uses the directory; kept separate from the editor list. */
export const REVEAL_IN_FOLDER: ExternalOpener = {
    id: "reveal",
    get label() { return tr("externalOpeners.revealInFolder"); },
    icon: FolderOpen,
    url: (p) => {
        const idx = p.lastIndexOf("/");
        const dir = idx > 0 ? p.slice(0, idx) : p;
        return `file://${encodeFilePath(dir)}`;
    },
};
